/**
 * Quote version export — the packet the backend returns from
 * GET /quotes/:orgId/:quoteId/versions/:versionId/export, and the pure
 * renderers the web and mobile apps share to turn it into clipboard text
 * (tab-separated), an HTML table, and the entries of an .xlsx workbook.
 *
 * Types mirror backend/src/utils/quoteVersionExport.ts (the backend does not
 * import this package). Nothing here touches the DOM or native APIs: each app
 * supplies its own clipboard write, zip step and file download / share.
 */

import type { OrgCurrency } from './types';
import { formatMoveDate } from './utils';

// ── Packet types ────────────────────────────────────────────────────────────

/**
 * How a cell should be formatted by the renderer. `date` cells carry the
 * stored string (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm`, or a full ISO timestamp);
 * text shows them the way the app does ("Nov 14, 2026 at 9:00 AM") and the
 * spreadsheet gets a real date cell.
 */
export type ExportCellKind = 'text' | 'money' | 'number' | 'hours' | 'percent' | 'date';

export interface ExportColumn {
  key: string;
  label: string;
  kind: ExportCellKind;
}

/** Raw cell value. Numbers stay numbers so spreadsheets receive typed cells. */
export type ExportCell = string | number | null;

export interface ExportTable {
  /** Optional caption shown above the table when a section has several. */
  title?: string;
  columns: ExportColumn[];
  rows: ExportCell[][];
  /** Key/value tables only: the kind of each row's value cell. */
  rowKinds?: ExportCellKind[];
}

export type ExportSectionKey = 'header' | 'lineItems' | 'crew' | 'materials' | 'scope' | 'totals';

export interface ExportSection {
  key: ExportSectionKey;
  title: string;
  tables: ExportTable[];
}

export interface QuoteVersionExportPacket {
  quoteVersionId: string;
  versionNumber: number;
  customerCompanyName: string;
  quoteDate: string;
  orgName: string;
  currency: OrgCurrency;
  currencySymbol: string;
  sections: {
    header: ExportSection;
    lineItems: ExportSection;
    /** `null` when the version predates crew snapshots; see `crewUnavailableReason`. */
    crew: ExportSection | null;
    /** `null` when the version carries no materials list. */
    materials: ExportSection | null;
    /** `null` when the version carries no scope-of-services list. */
    scope: ExportSection | null;
    totals: ExportSection;
  };
  crewUnavailableReason?: string;
}

// ── Copy options (what the export sheet offers) ─────────────────────────────

export type ExportCopyKey = 'lineItems' | 'crew' | 'materials' | 'scope' | 'all';

/** Human label used in the "Copied" confirmation. */
export const EXPORT_COPY_LABELS: Record<ExportCopyKey, string> = {
  lineItems: 'Line items',
  crew: 'Crew & trucks',
  materials: 'Materials',
  scope: 'Scope of services',
  all: 'Quote details',
};

/** Every non-null section in display order, for "Copy everything" and the spreadsheet. */
export function allExportSections(packet: QuoteVersionExportPacket): ExportSection[] {
  const s = packet.sections;
  return [s.header, s.lineItems, s.crew, s.materials, s.scope, s.totals].filter((x): x is ExportSection => x !== null);
}

/** Resolve which sections a copy key covers. `null` when the section is not on this version. */
export function exportSectionsForKey(packet: QuoteVersionExportPacket, key: ExportCopyKey): ExportSection[] | null {
  if (key === 'all') return allExportSections(packet);
  const section = packet.sections[key];
  return section ? [section] : null;
}

/** Why a copy option is disabled on this version, or `null` when it is available. */
export function exportUnavailableReason(packet: QuoteVersionExportPacket, key: ExportCopyKey): string | null {
  if (key === 'all') return null;
  if (packet.sections[key]) return null;
  if (key === 'crew') return packet.crewUnavailableReason ?? 'Not recorded on this version';
  if (key === 'materials') return 'No materials on this version';
  return 'No scope of services on this version';
}

// ── Cell formatting ─────────────────────────────────────────────────────────

function formatNumber(value: number, kind: ExportCellKind): string {
  switch (kind) {
    case 'money':
      return value.toFixed(2);
    case 'percent':
      // Rates are stored as decimals (0.0825 = 8.25%).
      return `${(value * 100).toFixed(2).replace(/\.?0+$/, '')}%`;
    case 'hours':
      return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/0$/, '');
    default:
      return String(value);
  }
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * A date cell the way the app shows it: "Nov 14, 2026" for dates,
 * "Nov 14, 2026 at 9:00 AM" when the stored value carries a time, and the
 * local calendar date for a full timestamp (expiration), like the app's
 * expiration banner. Anything unparseable is shown as stored.
 */
export function formatExportDate(value: string): string {
  if (DATE_ONLY.test(value) || DATE_TIME_LOCAL.test(value)) return formatMoveDate(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Plain text for a cell: numbers without currency symbols so they type into currency fields. */
function cellText(value: ExportCell, kind: ExportCellKind): string {
  if (value === null || value === undefined) return kind === 'date' ? '\u2014' : '';
  if (typeof value === 'number') return formatNumber(value, kind);
  if (kind === 'date') return formatExportDate(value);
  return value;
}

/** Display text for a cell in the HTML table: money gets the currency symbol. */
function cellDisplay(value: ExportCell, kind: ExportCellKind, sym: string): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && kind === 'money') {
    return `${sym}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return cellText(value, kind);
}

/** The kind that governs a cell: the row's kind on key/value tables, else the column's. */
function kindFor(table: ExportTable, rowIndex: number, colIndex: number): ExportCellKind {
  if (table.rowKinds && colIndex === table.columns.length - 1) return table.rowKinds[rowIndex] ?? 'text';
  return table.columns[colIndex]?.kind ?? 'text';
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Tabs and newlines inside a value would break the grid, so collapse them. */
function tsvSafe(s: string): string {
  return s.replace(/[\t\r\n]+/g, ' ');
}

/** First line of every payload, so a pasted block is self-identifying. */
export function exportIdentityLine(packet: QuoteVersionExportPacket): string {
  return `${packet.quoteVersionId} · ${packet.customerCompanyName}`;
}

// ── Text renderers ──────────────────────────────────────────────────────────

function tableToTsv(table: ExportTable): string {
  const lines: string[] = [];
  if (table.title) lines.push(tsvSafe(table.title));
  // Key/value tables read better without a "Field / Value" header row.
  if (!table.rowKinds) lines.push(table.columns.map(c => tsvSafe(c.label)).join('\t'));
  table.rows.forEach((row, r) => {
    lines.push(row.map((cell, c) => tsvSafe(cellText(cell, kindFor(table, r, c)))).join('\t'));
  });
  return lines.join('\n');
}

function tableToHtml(table: ExportTable, sym: string): string {
  const parts: string[] = [];
  if (table.title) parts.push(`<p><strong>${escapeHtml(table.title)}</strong></p>`);
  parts.push('<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse">');
  if (!table.rowKinds) {
    parts.push('<thead><tr>');
    for (const c of table.columns) {
      const align = c.kind === 'text' ? 'left' : 'right';
      parts.push(`<th align="${align}">${escapeHtml(c.label)}</th>`);
    }
    parts.push('</tr></thead>');
  }
  parts.push('<tbody>');
  table.rows.forEach((row, r) => {
    parts.push('<tr>');
    row.forEach((cell, c) => {
      const kind = kindFor(table, r, c);
      const align = kind === 'text' || typeof cell === 'string' ? 'left' : 'right';
      parts.push(`<td align="${align}">${escapeHtml(cellDisplay(cell, kind, sym))}</td>`);
    });
    parts.push('</tr>');
  });
  parts.push('</tbody></table>');
  return parts.join('');
}

/** Tab-separated text: pastes as cells into a spreadsheet, as aligned text anywhere else. */
export function exportSectionsToTsv(packet: QuoteVersionExportPacket, sections: ExportSection[]): string {
  const blocks: string[] = [exportIdentityLine(packet)];
  for (const section of sections) {
    const showTitle = sections.length > 1;
    const tables = section.tables.map(tableToTsv).join('\n\n');
    blocks.push(showTitle ? `${tsvSafe(section.title).toUpperCase()}\n${tables}` : tables);
  }
  return blocks.join('\n\n') + '\n';
}

/** HTML tables: pastes as a real table into Word, Google Docs, Gmail and rich-text editors. */
export function exportSectionsToHtml(packet: QuoteVersionExportPacket, sections: ExportSection[]): string {
  const sym = packet.currencySymbol;
  const parts: string[] = [`<p><strong>${escapeHtml(exportIdentityLine(packet))}</strong></p>`];
  for (const section of sections) {
    if (sections.length > 1) parts.push(`<h3>${escapeHtml(section.title)}</h3>`);
    parts.push(section.tables.map(t => tableToHtml(t, sym)).join('<br>'));
  }
  return parts.join('');
}

// ── Spreadsheet (.xlsx) entries ─────────────────────────────────────────────
// A minimal Office Open XML workbook: one worksheet per section, inline
// strings for text and typed number cells for money / hours / counts, so the
// file opens cleanly in Excel, Numbers and Google Sheets. This returns the
// zip entries; each app zips them with its own jszip.

export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function xmlEscape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/** Excel column letter for a 0-based index (0 → A, 26 → AA). */
function colLetter(index: number): string {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Style indexes in styles.xml: 0 default, 1 bold, 2 money (#,##0.00), 3 percent, 4 two decimals, 5 date, 6 date + time. */
const STYLE_BOLD = 1;
const STYLE_MONEY = 2;
const STYLE_PERCENT = 3;
const STYLE_DECIMAL = 4;
const STYLE_DATE = 5;
const STYLE_DATETIME = 6;

function styleFor(kind: ExportCellKind): number {
  if (kind === 'money') return STYLE_MONEY;
  if (kind === 'percent') return STYLE_PERCENT;
  if (kind === 'hours') return STYLE_DECIMAL;
  if (kind === 'date') return STYLE_DATE;
  return 0;
}

/** Days since 1899-12-30 (the Excel epoch) for a calendar date, plus the fraction of the day. */
function excelSerial(y: number, m: number, d: number, hours = 0, minutes = 0): number {
  const days = (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86_400_000;
  return days + hours / 24 + minutes / 1440;
}

/**
 * A stored date as a typed spreadsheet cell: the serial number plus the date
 * or date-time style. Full timestamps become the local calendar date, like
 * the text renderer. `null` when the value can't be parsed (written as text).
 */
function dateCell(value: string): { serial: number; style: number } | null {
  if (DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return { serial: excelSerial(y, m, d), style: STYLE_DATE };
  }
  if (DATE_TIME_LOCAL.test(value)) {
    const [datePart, timePart] = value.split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = timePart.split(':').map(Number);
    return { serial: excelSerial(y, m, d, hh, mm), style: STYLE_DATETIME };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { serial: excelSerial(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()), style: STYLE_DATE };
}

type SheetCell = { v: ExportCell; style?: number; kind?: ExportCellKind };

function sheetXml(rows: SheetCell[][]): string {
  const out: string[] = ['<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'];
  out.push('<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>');
  rows.forEach((row, r) => {
    out.push(`<row r="${r + 1}">`);
    row.forEach((cell, c) => {
      const ref = `${colLetter(c)}${r + 1}`;
      const style = cell.style ?? (cell.kind ? styleFor(cell.kind) : 0);
      const s = style ? ` s="${style}"` : '';
      if (cell.v === null || cell.v === undefined || cell.v === '') return;
      const date = cell.kind === 'date' && typeof cell.v === 'string' ? dateCell(cell.v) : null;
      if (typeof cell.v === 'number' && Number.isFinite(cell.v)) {
        out.push(`<c r="${ref}"${s}><v>${cell.v}</v></c>`);
      } else if (date) {
        out.push(`<c r="${ref}" s="${date.style}"><v>${date.serial}</v></c>`);
      } else {
        out.push(`<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${xmlEscape(String(cell.v))}</t></is></c>`);
      }
    });
    out.push('</row>');
  });
  out.push('</sheetData></worksheet>');
  return out.join('');
}

function sectionToSheetRows(packet: QuoteVersionExportPacket, section: ExportSection): SheetCell[][] {
  const rows: SheetCell[][] = [
    [{ v: exportIdentityLine(packet), style: STYLE_BOLD }],
    [],
  ];
  section.tables.forEach((table, t) => {
    if (t > 0) rows.push([]);
    if (table.title) rows.push([{ v: table.title, style: STYLE_BOLD }]);
    if (!table.rowKinds) rows.push(table.columns.map(c => ({ v: c.label, style: STYLE_BOLD })));
    table.rows.forEach((row, r) => {
      rows.push(row.map((cell, c) => ({ v: cell, kind: kindFor(table, r, c) })));
    });
  });
  return rows;
}

/** Sheet names: max 31 chars, none of  : \ / ? * [ ] */
function sheetName(title: string): string {
  return title.replace(/[:\\/?*[\]]/g, '').slice(0, 31) || 'Sheet';
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="0.00%"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="7">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="2" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="14" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="22" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

export interface XlsxEntry {
  path: string;
  content: string;
}

/** The files of an .xlsx workbook (one sheet per section), ready to be zipped. */
export function buildXlsxEntries(packet: QuoteVersionExportPacket): XlsxEntry[] {
  const sections = allExportSections(packet);
  const sheets = sections.map((section, i) => ({
    id: i + 1,
    name: sheetName(section.title),
    xml: sheetXml(sectionToSheetRows(packet, section)),
  }));

  const entries: XlsxEntry[] = [];

  entries.push({ path: '[Content_Types].xml', content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${sheets.map(s => `<Override PartName="/xl/worksheets/sheet${s.id}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
</Types>` });

  entries.push({ path: '_rels/.rels', content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>` });

  entries.push({ path: 'xl/workbook.xml', content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>
${sheets.map(s => `<sheet name="${xmlEscape(s.name)}" sheetId="${s.id}" r:id="rId${s.id}"/>`).join('\n')}
</sheets>
</workbook>` });

  entries.push({ path: 'xl/_rels/workbook.xml.rels', content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheets.map(s => `<Relationship Id="rId${s.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${s.id}.xml"/>`).join('\n')}
<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>` });

  entries.push({ path: 'xl/styles.xml', content: STYLES_XML });
  for (const s of sheets) entries.push({ path: `xl/worksheets/sheet${s.id}.xml`, content: s.xml });

  return entries;
}

/** File name like `QT-00001-2 Acme Corp.xlsx`. */
export function xlsxFileName(packet: QuoteVersionExportPacket): string {
  const customer = packet.customerCompanyName.replace(/[\\/:*?"<>|]+/g, '').trim();
  return `${packet.quoteVersionId}${customer ? ` ${customer}` : ''}.xlsx`;
}
